import { useEffect, useMemo, useState } from 'react';
import { Card } from '@iwoca/orion';
import clsx from 'clsx';
import { InformationMessage } from 'components/InformationMessage/InformationMessage';
import { useOffers } from 'components/Signup/hooks/useOffers';
import { useAmplitude } from 'hooks/useAmplitude';
import { useLocale } from 'hooks/useLocale';
import { useNumberFormat } from 'hooks/useNumberFormat';
import { TOfferDataRequirement } from 'lendingApi/lapiOverrides.types';
import _ from 'lodash';
import { useNavigate } from 'react-router-dom';
import { REQUESTED_AMOUNT_CONTACT_THRESHOLD } from 'utils/applications/signup.config';
import { PATH_NAMES } from 'utils/routes/pathNames';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import { useSignupData } from '../hooks/useSignupData';
import { LoanPurposeDetails } from './LoanPurposeDetails/LoanPurposeDetails';
import { AgedDebtorReport } from './RequirementTypes/AgedDebtorReport';
import { BankStatements } from './RequirementTypes/BankStatements/BankStatements';
import { BusinessPlan } from './RequirementTypes/BusinessPlan';
import { CompanyAccounts } from './RequirementTypes/CompanyAccounts';
import { ManagementAccounts } from './RequirementTypes/ManagementAccounts';
import { OtherDocuments } from './RequirementTypes/OtherDocuments';
import { VATReturns } from './RequirementTypes/VATReturns';
import { ShareRequirements } from './ShareRequirements/ShareRequirements';
import { SubmitDocuments } from './SubmitDocuments/SubmitDocuments';
import styles from './TradingHistory.module.css';
import { TradingHistoryLoader } from './TradingHistoryLoader';

export const TradingHistory = () => {
  const { isUK } = useLocale();
  const navigate = useNavigate();
  const [purposeDetails, setPurposeDetails] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);

  const {
    applicationId,
    documentRequirements,
    isLoadingOffers,
    isRefetchingOffers,
    autoBankStatements,
  } = useOffers({
    getOffersEnabled: true,
  });

  const { updateLoanPurposeDetails, applicationData, stateData } =
    useSignupData();
  const { trackEvent } = useAmplitude();
  const { formatCurrency } = useNumberFormat();

  const amountIsAboveContactThreshold =
    Number(applicationData?.amount) >= REQUESTED_AMOUNT_CONTACT_THRESHOLD;

  const hasDocumentsRequired = useMemo(
    () =>
      Boolean(
        documentRequirements?.find((action) => {
          return action.status === 'required' && action.is_optional === false;
        }),
      ),
    [documentRequirements],
  );

  useEffect(() => {
    if (applicationData.loanPurposeDetails) {
      setPurposeDetails(applicationData.loanPurposeDetails);
    }
  }, [applicationData.loanPurposeDetails]);

  useEffect(() => {
    if (Boolean(documentRequirements) && !hasDocumentsRequired) {
      navigate(
        PATH_NAMES.application({ applicationId: applicationId as string }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Boolean(documentRequirements), hasDocumentsRequired]);

  const DOCUMENT_REQUIREMENTS: { [key: string]: React.ElementType } = {
    company_accounts: CompanyAccounts,
    management_accounts: ManagementAccounts,
    business_plan: BusinessPlan,
    aged_debtor_report: AgedDebtorReport,
    vat_return: VATReturns,
    bank_statement: BankStatements,
    via_open_banking: BankStatements,
  };

  const handleUpdateApplication = async () => {
    await updateLoanPurposeDetails(purposeDetails);
  };

  const renderDocumentRequirements = (
    requirements: TOfferDataRequirement['actions'],
  ) => {
    return requirements?.map((requirement) => {
      if (!requirement.type || !DOCUMENT_REQUIREMENTS[requirement.type]) {
        return null;
      }

      const RequirementDetails = DOCUMENT_REQUIREMENTS[requirement.type];

      return (
        <RequirementDetails
          key={requirement.type}
          isOptional={requirement.is_optional}
          quantifier={requirement.quantifier?.value}
          resolutionMethods={requirement.resolution_methods}
          status={requirement.status}
          isSubmitted={isSubmitted}
          customerDetails={{
            email: stateData?.applicant?.email,
            firstName: stateData?.applicant?.firstName,
          }}
        />
      );
    });
  };

  const isLoading =
    isLoadingOffers || (isRefetchingOffers && !documentRequirements);

  const renderContents = () => {
    if (isLoading) {
      return <TradingHistoryLoader />;
    }

    if (!documentRequirements) {
      return;
    }

    const optionalDocumentRequirements = documentRequirements.filter(
      (document) => document.is_optional,
    );

    const requiredDocumentRequirements = documentRequirements?.filter(
      (document) => !document.is_optional,
    );

    trackEvent(AMPLITUDE_EVENTS.documentRequirementsLoaded, {
      'Optional documents': optionalDocumentRequirements
        ?.map((doc) => _.startCase(doc.type))
        .join(', '),
      'Required documents': requiredDocumentRequirements
        ?.map((doc) => _.startCase(doc.type))
        .join(', '),
    });

    return (
      <>
        <p className={styles.subHeading}>
          1. Please provide more specific information about the purpose of this
          loan.
        </p>
        {applicationData.loanPurpose && (
          <LoanPurposeDetails
            purpose={applicationData.loanPurpose}
            purposeDetails={purposeDetails}
            setPurposeDetails={setPurposeDetails}
          />
        )}
        <p className={styles.subHeading}>
          2. Add supporting documents to continue.
        </p>
        <Card className={styles.cardContainer}>
          <div>
            {renderDocumentRequirements(requiredDocumentRequirements)}
            <hr className={styles.seperator} />
          </div>
          <div>
            <h4 className={styles.title}>Optional documents</h4>
            {renderDocumentRequirements(optionalDocumentRequirements)}
            {/* Using other documents on DE will trigger manual review */}
            {/* Do not use on DE unless this is intended */}
            <OtherDocuments />
          </div>
        </Card>
        <Card className={clsx(styles.cardContainer, styles.shareReqCard)}>
          <ShareRequirements
            requiredDocs={requiredDocumentRequirements}
            optionalDocs={optionalDocumentRequirements}
          />
        </Card>
        {amountIsAboveContactThreshold && isUK && (
          <InformationMessage
            alignCenter={true}
            message="For request amounts of £350,000 and above, our underwriting team may contact the applicant directly. We’ll notify you in advance."
          />
        )}
        <SubmitDocuments
          isSubmitted={isSubmitted}
          setIsSubmitted={setIsSubmitted}
          updateApplication={handleUpdateApplication}
        />
      </>
    );
  };

  return (
    <div className={styles.tradingHistory}>
      <div className={styles.headingContainer}>
        <h1 className={styles.heading}>
          You're requesting a <b>{formatCurrency(applicationData.amount)}</b>{' '}
          loan for a <b>{applicationData.loanDuration?.replace(/-/g, ' ')} </b>
          term
        </h1>
        <p className={styles.mainSubHeading}>
          {isLoading && 'Loading your required documents...'}
          {!isLoading &&
            autoBankStatements &&
            'We are not able to provide an instant offer for this particular application. However, we will be able to review this application within 24 hours following a submission of bank statements.'}
        </p>
      </div>
      {renderContents()}
    </div>
  );
};
